/**
 * Member detail slide-over — change role + remove + view profile.
 *
 * Web port of the mobile RolePickerSheet + member-detail flow.
 * One panel handles BOTH joined members and pending invites:
 *   - Joined: name + phone + role pill + change-role chips + Remove
 *   - Pending: name + phone (display only) + "INVITE PENDING" pill
 *     + change-role chips (re-issues the invite with new role) +
 *     Revoke
 *
 * Role changes call the existing `setMemberRoleCall` callable.
 * Removals call `removeMember` (which takes either `uid` for joined
 * or `phoneNumber` for pending).
 */
import { useEffect, useState } from 'react';

import {
  ROLE_LABELS,
  assignableRolesFor,
  type AssignableRole,
  type RoleKey,
} from '@shared/perms';

// `assignableFor` was a thin wrapper around the three
// ASSIGNABLE_ROLES_BY_* constants. The shared package exports
// `assignableRolesFor(callerRole)` which does exactly the same
// thing, so we just re-export it under the old local name to keep
// the JSX downstream unchanged.
const assignableFor = assignableRolesFor;
import {
  ROLE_TONE,
  memberInitials,
  type OrgMember,
} from '@/lib/useOrgMembers';
import type { PendingInvite } from '@/lib/usePendingInvites';
import {
  removeProjectMember,
  setMemberRoleCall,
} from '@/lib/teamMembers';

export type MemberDetailMode =
  | { kind: 'closed' }
  | { kind: 'member'; member: OrgMember }
  | { kind: 'invite'; invite: PendingInvite };

export type MemberDetailSlideOverProps = {
  mode: MemberDetailMode;
  orgId: string | null;
  onClose: () => void;
  /** Disable role-change + remove (e.g. the viewer is a client). */
  readOnly?: boolean;
  /** Caller's role in this org. Drives:
   *   - which roles appear in the chip grid (Manager only sees
   *     sub-roles per `ASSIGNABLE_BY_MANAGER`)
   *   - peer-protection: when caller is Manager and target is
   *     Super Admin / Admin / Manager, the role picker + Remove
   *     button are replaced with a "Only Admin can change this
   *     user" inline message. */
  callerRole?: RoleKey | null;
};

export function MemberDetailSlideOver({
  mode,
  orgId,
  onClose,
  readOnly = false,
  callerRole = null,
}: MemberDetailSlideOverProps) {
  const open = mode.kind !== 'closed';
  const isInvite = mode.kind === 'invite';
  const isOwner = mode.kind === 'member' && mode.member.isOwner;

  // Pull the canonical role + name from whichever payload we have.
  const currentRole: RoleKey =
    mode.kind === 'member'
      ? mode.member.role
      : mode.kind === 'invite'
        ? mode.invite.role
        : 'viewer';

  // Caller-derived gates.
  //
  // assignableForCaller: which roles the chip grid should offer.
  //   Server-side `setMemberRole` also enforces this, but matching
  //   the UI prevents a confused-tap → permission-denied flow.
  //
  // canEditTarget: false when a Manager opens a Super Admin / Admin
  //   / Manager peer (peer protection). The picker + Remove button
  //   are hidden + replaced with an inline explanatory message.
  //   Admin / SA callers are never restricted by this (the
  //   server-side rule already locks Admin→Admin transitions to SA).
  const assignableForCaller = assignableFor(callerRole);
  const canEditTarget =
    !(callerRole === 'manager' &&
      (currentRole === 'superAdmin' ||
        currentRole === 'admin' ||
        currentRole === 'manager'));
  const displayName =
    mode.kind === 'member'
      ? mode.member.displayName
      : mode.kind === 'invite'
        ? (mode.invite.displayName ?? mode.invite.phoneNumber)
        : '';
  const phoneNumber =
    mode.kind === 'member'
      ? mode.member.phoneNumber
      : mode.kind === 'invite'
        ? mode.invite.phoneNumber
        : null;
  const email =
    mode.kind === 'member' ? mode.member.email : null;
  const photoURL =
    mode.kind === 'member' ? mode.member.photoURL : null;

  // Editable role draft. Reset whenever the slide-over re-opens for
  // a different target.
  const [draftRole, setDraftRole] = useState<RoleKey>(currentRole);
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDraftRole(currentRole);
    setBusy(false);
    setConfirmRemove(false);
    setError(null);
  }, [open, currentRole]);

  // Body scroll-lock + Esc.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, busy, onClose]);

  const dirty = draftRole !== currentRole;

  async function handleSaveRole() {
    if (!orgId || !dirty) return;
    setBusy(true);
    setError(null);
    try {
      if (mode.kind === 'member') {
        await setMemberRoleCall({
          orgId,
          uid: mode.member.uid,
          role: draftRole as AssignableRole,
        });
      } else if (mode.kind === 'invite') {
        // The simplest reliable way to "edit" a pending invite is to
        // remove + re-issue (the server's `inviteMember` callable
        // upserts; calling `removeMember` first ensures any project
        // scoping is reset cleanly).
        // For now we surface a note; full re-issue would need the
        // invite's project list which isn't on the panel — keep
        // role edits to joined members for v1.
        setError(
          'Editing a pending invite role isn’t supported yet. Revoke the invite and re-invite with the new role.',
        );
        return;
      }
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove() {
    if (!orgId) return;
    setBusy(true);
    setError(null);
    try {
      if (mode.kind === 'member') {
        await removeProjectMember({ orgId, uid: mode.member.uid });
      } else if (mode.kind === 'invite') {
        await removeProjectMember({
          orgId,
          phoneNumber: mode.invite.phoneNumber,
        });
      }
      onClose();
    } catch (e) {
      setError((e as Error).message);
      setConfirmRemove(false);
    } finally {
      setBusy(false);
    }
  }

  // Role tone for the avatar tint.
  const tone = ROLE_TONE[currentRole];

  return (
    <>
      <div
        className={`slide-overlay${open ? ' open' : ''}`}
        onClick={() => !busy && onClose()}
      />
      <div className={`slide-over${open ? ' open' : ''}`}>
        <div className="slide-header">
          <h2 className="slide-title">
            <i className="ph ph-user" />{' '}
            {isInvite ? 'Pending invite' : 'Team member'}
          </h2>
          <button
            type="button"
            className="slide-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            <i className="ph ph-x" />
          </button>
        </div>

        <div className="slide-body">
          {error ? (
            <div
              style={{
                background: '#FEF2F2',
                color: 'var(--critical)',
                padding: 12,
                borderRadius: 6,
                fontSize: 13,
                border: '1px solid #FECACA',
              }}
            >
              {error}
            </div>
          ) : null}

          {/* Hero card — avatar + name + role pill */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              padding: 16,
              border: '1px solid var(--border)',
              borderRadius: 10,
              background: '#FAFAFA',
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: photoURL
                  ? `center / cover no-repeat url(${photoURL})`
                  : tone.bg,
                color: tone.fg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
                fontWeight: 700,
                flexShrink: 0,
                border: photoURL ? '1px solid var(--border)' : undefined,
              }}
            >
              {photoURL ? '' : memberInitials(displayName)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {displayName}
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 6,
                  alignItems: 'center',
                  marginTop: 4,
                  flexWrap: 'wrap',
                }}
              >
                <span
                  className="stage-pill"
                  style={{
                    background: tone.bg,
                    color: tone.fg,
                    fontWeight: 700,
                    fontSize: 10,
                    padding: '2px 8px',
                  }}
                >
                  {ROLE_LABELS[currentRole]}
                </span>
                {isOwner ? (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: 'var(--text-tertiary)',
                      letterSpacing: 0.5,
                    }}
                  >
                    · STUDIO OWNER
                  </span>
                ) : null}
                {isInvite ? (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: 999,
                      background: '#FEF3C7',
                      color: '#B45309',
                      letterSpacing: 0.4,
                    }}
                  >
                    INVITE PENDING
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          {/* Contact rows */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
              border: '1px solid var(--border)',
              borderRadius: 10,
              overflow: 'hidden',
            }}
          >
            <ContactRow label="Phone" value={phoneNumber} icon="ph ph-phone" copyable />
            {email ? (
              <ContactRow label="Email" value={email} icon="ph ph-envelope" />
            ) : null}
          </div>

          {/* Role chip grid — hidden for owner + read-only viewers
              + peer-protected members (Manager opening Mgr/Admin/SA). */}
          {!isOwner && !readOnly && canEditTarget && assignableForCaller.length > 0 ? (
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'var(--text-tertiary)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  marginBottom: 8,
                }}
              >
                Change role
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 6,
                }}
              >
                {assignableForCaller.map((r) => {
                  const active = r === draftRole;
                  const t = ROLE_TONE[r];
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setDraftRole(r)}
                      style={{
                        padding: '8px 10px',
                        border: active
                          ? `1.5px solid ${t.fg}`
                          : '1px solid var(--border)',
                        background: active ? t.bg : '#fff',
                        color: active ? t.fg : 'var(--text-secondary)',
                        borderRadius: 8,
                        fontSize: 12,
                        fontWeight: active ? 700 : 500,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        textAlign: 'left',
                      }}
                    >
                      {ROLE_LABELS[r]}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* Manager peer-protection notice — shown in place of the
              picker when a Manager opens a Super Admin / Admin /
              Manager member. */}
          {!isOwner && !readOnly && !canEditTarget ? (
            <div
              style={{
                padding: 12,
                borderRadius: 8,
                background: '#FEF3C7',
                border: '1px solid #FDE68A',
                color: '#92400E',
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              <strong>Only Admin can change this user's role.</strong>{' '}
              Managers can edit + remove Accountant, Site Engineer,
              Supervisor, Designer, Viewer, and Client members only.
            </div>
          ) : null}

          {/* Footer actions */}
          <div
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 8,
              paddingTop: 16,
              borderTop: '1px solid var(--border)',
            }}
          >
            {!isOwner && !readOnly && canEditTarget ? (
              confirmRemove ? (
                <div
                  style={{
                    display: 'flex',
                    gap: 6,
                    alignItems: 'center',
                  }}
                >
                  <span
                    style={{
                      fontSize: 12,
                      color: 'var(--critical)',
                    }}
                  >
                    {isInvite ? 'Revoke invite?' : 'Remove from studio?'}
                  </span>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setConfirmRemove(false)}
                    disabled={busy}
                  >
                    No
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleRemove}
                    disabled={busy}
                    style={{
                      background: 'var(--critical)',
                      borderColor: 'var(--critical)',
                    }}
                  >
                    {busy
                      ? 'Working…'
                      : isInvite
                        ? 'Yes, revoke'
                        : 'Yes, remove'}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setConfirmRemove(true)}
                  disabled={busy}
                  style={{ color: 'var(--critical)' }}
                >
                  <i className="ph ph-trash" />{' '}
                  {isInvite ? 'Revoke invite' : 'Remove'}
                </button>
              )
            ) : (
              <div />
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={onClose}
                disabled={busy}
              >
                Close
              </button>
              {!isOwner && !readOnly && canEditTarget ? (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleSaveRole}
                  disabled={busy || !dirty}
                >
                  {busy ? 'Saving…' : 'Save role'}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ───── Row helpers ─────

function ContactRow({
  label,
  value,
  icon,
  copyable = false,
}: {
  label: string;
  value: string | null;
  icon: string;
  copyable?: boolean;
}) {
  if (!value) return null;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        borderTop: '1px solid var(--border)',
      }}
    >
      <i
        className={icon}
        style={{ fontSize: 14, color: 'var(--text-tertiary)', width: 16, textAlign: 'center' }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 11,
            color: 'var(--text-tertiary)',
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            fontWeight: 700,
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: 13,
            color: 'var(--text-primary)',
            fontWeight: 500,
            marginTop: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {value}
        </div>
      </div>
      {copyable ? (
        <button
          type="button"
          onClick={() => {
            try {
              navigator.clipboard?.writeText(value);
            } catch {
              /* clipboard may be unavailable in insecure contexts */
            }
          }}
          title="Copy"
          style={{
            background: 'transparent',
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: '4px 8px',
            fontSize: 11,
            color: 'var(--text-secondary)',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          <i className="ph ph-copy" />
        </button>
      ) : null}
    </div>
  );
}
